import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { ShieldAlert, Loader2, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";

type SanitizeEvent = {
  id: string;
  field: string;
  action: "blocked" | "sanitized";
  reason: string;
  reason_code: string;
  sample: string | null;
  original_length: number;
  sanitized_length: number;
  source: string;
  fingerprint: string;
  created_at: string;
};

const REPEAT_WINDOW_MS = 24 * 60 * 60 * 1000;
const REPEAT_THRESHOLD = 3;

export function TrendSanitizeEventsPanel() {
  const [events, setEvents] = useState<SanitizeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("trend_sanitize_events" as any)
      .select(
        "id, field, action, reason, reason_code, sample, original_length, sanitized_length, source, fingerprint, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(200);
    setLoading(false);
    if (error) return toast.error(`Load sanitize events: ${error.message}`);
    setEvents((data ?? []) as unknown as SanitizeEvent[]);
  };

  useEffect(() => {
    void load();
  }, []);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) =>
      [e.field, e.action, e.reason, e.reason_code, e.sample ?? ""].join(" ").toLowerCase().includes(q),
    );
  }, [events, filter]);

  const repeats = useMemo(() => {
    const counts = new Map<string, { count: number; reason: string; field: string }>();
    for (const e of events) {
      if (Date.now() - new Date(e.created_at).getTime() > REPEAT_WINDOW_MS) continue;
      const prev = counts.get(e.fingerprint);
      counts.set(e.fingerprint, {
        count: (prev?.count ?? 0) + 1,
        reason: e.reason,
        field: e.field,
      });
    }
    return [...counts.entries()]
      .filter(([, v]) => v.count >= REPEAT_THRESHOLD)
      .sort((a, b) => b[1].count - a[1].count);
  }, [events]);

  const exportCsv = () => {
    const header = [
      "created_at", "field", "action", "reason_code", "reason",
      "original_length", "sanitized_length", "source", "fingerprint", "sample",
    ];
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = visible.map((e) =>
      [
        e.created_at, e.field, e.action, e.reason_code, e.reason,
        e.original_length, e.sanitized_length, e.source, e.fingerprint, e.sample ?? "",
      ].map(esc).join(","),
    );
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trend-sanitize-events-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="size-4" /> Trend input sanitization
          </CardTitle>
          <CardDescription>
            Every niche, title, summary, or tag value the ingestion job blocked or cleaned before writing to
            trends. Repeat fingerprints usually mean a recurring prompt-injection attempt in the source signals.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!visible.length}>
            <Download className="size-3.5 mr-1" /> CSV
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh sanitize events">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {repeats.length > 0 && (
          <Alert variant="destructive" className="border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100">
            <ShieldAlert className="size-4" />
            <AlertTitle>Recurring suspicious input</AlertTitle>
            <AlertDescription>
              {repeats.slice(0, 3).map(([fp, v]) => (
                <div key={fp} className="font-mono text-xs">
                  {v.count}× {v.field} — {v.reason}
                </div>
              ))}
            </AlertDescription>
          </Alert>
        )}

        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by field, reason, or sample…"
          aria-label="Filter sanitize events"
        />

        {!loading && visible.length === 0 && (
          <p className="text-sm text-muted-foreground">No blocked or sanitized trend inputs recorded.</p>
        )}

        <div className="space-y-2">
          {visible.map((e) => (
            <div key={e.id} className="rounded-lg border border-border p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={e.action === "blocked" ? "destructive" : "secondary"}>{e.action}</Badge>
                <span className="font-medium">{e.field}</span>
                <span className="text-muted-foreground">{e.reason}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </div>
              {e.sample && (
                <pre className="mt-2 whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-[11px]">
                  {e.sample}
                </pre>
              )}
              <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                {e.reason_code} · {e.source} · {e.original_length}→{e.sanitized_length} chars · {e.fingerprint}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default TrendSanitizeEventsPanel;
